import React, { useEffect, useState } from "react";
import axios from "axios";
import { getAdminToken } from "../services/authService";
import { useTheme } from "../ThemeContext";
import {
  Card,
  Form,
  Alert,
  Spinner,
  Button,
  Row,
  Col,
  Collapse,
  Nav,
  Tab,
} from "react-bootstrap";
import { TbRefresh, TbShield, TbShieldCheck } from "react-icons/tb";

// Two kinds of module share the scan_modules table, and this screen used to list them as
// one undifferentiated column under "Scan Module Settings":
//
//  • Unified Scanner — what the desktop scanner app runs (Localhost, Network, Active
//    Directory, Microsoft 365, PII). Their sub-switches are individual scan checks.
//  • Platform Features — parts of the web app a plan includes or not. These names are the
//    database keys used by every service's utils/entitlements.js; they must match exactly.
//
// Anything not listed as a platform feature is treated as a scanner module, so a scanner
// module added later lands in the right group without a change here.
export const PLATFORM_FEATURES = [
  { name: "Lead Generation", label: "Lead Generation",
    desc: "The whole Lead Generation area, with a switch for each part: Lead Magnets (including Saved widgets and embedded widgets), Prospecting, External Risk Scan and Generate Scan Link." },
  { name: "Snapshot Scanner", label: "Snapshot Scanner",
    desc: "Sending clients a one-time Pipsqueak Snapshot download link from an organization." },
  { name: "Build-Your-Own-Reports", label: "Build Your Own Reports",
    desc: "Creating custom report templates with the report builder." },
  { name: "AI Reports", label: "AI Reports",
    desc: "AI-generated report layouts and written reports in the report builder." },
  { name: "Business Reviews", label: "Business Reviews",
    desc: "The whole Business Reviews section: assessments, schedules and technology health." },
];
const PLATFORM_BY_NAME = new Map(PLATFORM_FEATURES.map((f, i) => [f.name, { ...f, order: i }]));

const ScanModules = () => {
  const { isDarkMode } = useTheme();
  const [licenseModules, setLicenseModules] = useState([]);
  const [activeLicense, setActiveLicense] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const themeStyles = {
    backgroundColor: "var(--v2-bg)",
    color: "var(--v2-text)",
  };

  const cardThemeStyles = {
    backgroundColor: "var(--v2-surface)",
    color: "var(--v2-text)",
    border: "none",
    boxShadow: isDarkMode
      ? "0 4px 6px rgba(0, 0, 0, 0.3)"
      : "0 4px 6px rgba(0, 0, 0, 0.1)",
  };

  const buildModulesFromObject = (moduleObject = {}) =>
    Object.entries(moduleObject).map(([moduleName, module]) => ({
      id: module.id || moduleName,
      name: moduleName,
      enabled: Boolean(module.enabled),
      features: Object.entries(module.features || {}).map(
        ([featureName, isEnabled]) => ({
          id: featureName,
          name: featureName,
          enabled: Boolean(isEnabled),
        })
      ),
    }));

  const toDisplayName = (value) =>
    (value || "")
      .split(/[\s_-]+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const normalizeLicenseTypes = (items = []) =>
    items.map((license) => ({
      ...license,
      scopeType: "license",
      displayName: license.displayName || toDisplayName(license.name),
      modules: (license.modules || []).map((module) => ({
        ...module,
        features: module.features || [],
      })),
    }));

  const fetchModules = async () => {
    try {
      setLoading(true);
      const token = getAdminToken();
      const headers = { Authorization: `Bearer ${token}` };

      const [allResponse, basicResponse, proResponse] = await Promise.all([
        // scope=all: this screen also manages the web-app plan features. Without it the
        // endpoint returns only the desktop scanner's view (utils/scanModulesCatalog.js).
        axios
          .get("/s1/scan-modules", { headers, params: { scope: "all" } })
          .then((res) => res.data)
          .catch(() => null),
        axios
          .get("/s1/scan-modules", {
            headers,
            params: { licenseType: "community", scope: "all" },
          })
          .then((res) => res.data)
          .catch(() => null),
        axios
          .get("/s1/scan-modules", {
            headers,
            params: { licenseType: "pro", scope: "all" },
          })
          .then((res) => res.data)
          .catch(() => null),
      ]);

      const licenseMap = new Map();

      if (allResponse && Array.isArray(allResponse.licenseTypes)) {
        normalizeLicenseTypes(allResponse.licenseTypes).forEach((license) => {
          licenseMap.set(license.name, license);
        });
      }

      const handleScopedPayload = (payload, licenseName) => {
        if (!payload || !licenseName) return;

        if (Array.isArray(payload.licenseTypes) && payload.licenseTypes.length) {
          normalizeLicenseTypes(payload.licenseTypes).forEach((license) => {
            licenseMap.set(license.name, license);
          });
          return;
        }

        if (typeof payload === "object" && payload !== null) {
          licenseMap.set(licenseName, {
            id: licenseName,
            name: licenseName,
            displayName: toDisplayName(licenseName),
            scopeType: "license",
            modules: buildModulesFromObject(payload),
          });
        }
      };

      handleScopedPayload(basicResponse, "community");
      handleScopedPayload(proResponse, "pro");

      let normalized = Array.from(licenseMap.values());

      if (!normalized.length && typeof allResponse === "object" && allResponse) {
        normalized = [
          {
            id: "global",
            name: "global",
            displayName: "All Modules",
            scopeType: "global",
            modules: buildModulesFromObject(allResponse),
          },
        ];
      }

      setLicenseModules(normalized);

      if (normalized.length) {
        setActiveLicense((prev) => {
          if (prev && normalized.some((lt) => lt.name === prev)) {
            return prev;
          }
          return normalized[0].name;
        });
      } else {
        setActiveLicense(null);
      }

      setMessage("Modules loaded successfully.");
    } catch (err) {
      console.error(err);
      setError("Failed to fetch scan modules.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModules();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setError("");
      setMessage("");
    }, 4000);
    return () => clearTimeout(timeout);
  }, [error, message]);

  const toggleMainModule = async (licenseName, moduleName, currentStatus) => {
    try {
      const license = licenseModules.find((item) => item.name === licenseName);
      const token = getAdminToken();
      if (license && license.scopeType === "license") {
        await axios.put(
          `/s1/admin/license-types/${encodeURIComponent(
            licenseName
          )}/modules/${encodeURIComponent(moduleName)}`,
          { is_enabled: !currentStatus },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.put(
          `/s1/admin/scan-modules/${encodeURIComponent(moduleName)}`,
          { is_enabled: !currentStatus },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setLicenseModules((prev) =>
        prev.map((license) =>
          license.name === licenseName
            ? {
                ...license,
                modules: (license.modules || []).map((module) =>
                  module.name === moduleName
                    ? { ...module, enabled: !currentStatus }
                    : module
                ),
              }
            : license
        )
      );

      if (license && license.scopeType === "license") {
        setMessage(
          `Module "${moduleName}" updated for ${
            license.displayName || toDisplayName(licenseName)
          }.`
        );
      } else {
        setMessage(`Module "${moduleName}" updated.`);
      }
    } catch (err) {
      console.error(err);
      setError(`Failed to update module "${moduleName}".`);
    }
  };

  const toggleSubFeature = async (
    licenseName,
    moduleName,
    featureName,
    currentStatus
  ) => {
    try {
      const license = licenseModules.find((item) => item.name === licenseName);
      const token = getAdminToken();
      const endpoint =
        license && license.scopeType === "license"
          ? `/s1/admin/license-types/${encodeURIComponent(
              licenseName
            )}/modules/${encodeURIComponent(moduleName)}/features`
          : `/s1/admin/scan-modules/${encodeURIComponent(moduleName)}/features`;

      await axios.put(
        endpoint,
        {
          featureName,
          is_enabled: !currentStatus,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLicenseModules((prev) =>
        prev.map((license) =>
          license.name === licenseName
            ? {
                ...license,
                modules: (license.modules || []).map((module) =>
                  module.name === moduleName
                    ? {
                        ...module,
                        features: (module.features || []).map((feature) =>
                          feature.name === featureName
                            ? { ...feature, enabled: !currentStatus }
                            : feature
                        ),
                      }
                    : module
                ),
              }
            : license
        )
      );

      if (license && license.scopeType === "license") {
        setMessage(
          `Feature "${featureName}" in "${moduleName}" updated for ${
            license.displayName || toDisplayName(licenseName)
          }.`
        );
      } else {
        setMessage(`Feature "${featureName}" in "${moduleName}" updated.`);
      }
    } catch (err) {
      console.error(err);
      setError(`Failed to update feature "${featureName}".`);
    }
  };

  // One module and its sub-switches. `label`/`desc` override the raw database name.
  const ModuleRow = ({ licenseName, module, label, desc }) => {
    const hasFeatures = module.features && module.features.length > 0;
    return (
      <div className="mb-4">
        <Form.Check
          type="switch"
          id={`switch-${licenseName}-${module.name}`}
          label={<strong>{label || module.name}</strong>}
          checked={module.enabled}
          onChange={() => toggleMainModule(licenseName, module.name, module.enabled)}
          className={`mb-1 ${isDarkMode ? "text-light" : "text-dark"}`}
        />
        {desc && (
          <div className={`small ms-5 mb-2 ${isDarkMode ? "text-light" : "text-muted"}`} style={{ opacity: 0.8 }}>
            {desc}
          </div>
        )}
        {hasFeatures && (
          <Collapse in={module.enabled}>
            <div className="ms-4">
              {module.features.map((feature) => (
                <Form.Check
                  key={`${licenseName}-${module.name}-${feature.name}`}
                  type="switch"
                  id={`switch-${licenseName}-${module.name}-${feature.name}`}
                  label={feature.name}
                  checked={feature.enabled}
                  onChange={() => toggleSubFeature(licenseName, module.name, feature.name, feature.enabled)}
                  className={`mb-2 ${isDarkMode ? "text-light" : "text-dark"}`}
                />
              ))}
            </div>
          </Collapse>
        )}
      </div>
    );
  };

  const GroupHeading = ({ title, note }) => (
    <div className="mb-3 pb-2" style={{ borderBottom: isDarkMode ? "1px solid #444" : "1px solid #dee2e6" }}>
      <h6 className={`mb-1 ${isDarkMode ? "text-light" : "text-dark"}`} style={{ fontWeight: 700 }}>{title}</h6>
      <div className={`small ${isDarkMode ? "text-light" : "text-muted"}`} style={{ opacity: 0.85 }}>{note}</div>
    </div>
  );

  // The modules of one plan, split into the two groups described at the top of the file.
  const ModuleList = ({ licenseName, modules }) => {
    if (!modules || modules.length === 0) {
      return (
        <div className="text-center py-4">
          <div className={`text-muted ${isDarkMode ? "text-light" : ""}`}>No modules available.</div>
        </div>
      );
    }

    const platform = modules
      .filter((m) => PLATFORM_BY_NAME.has(m.name))
      .sort((a, b) => PLATFORM_BY_NAME.get(a.name).order - PLATFORM_BY_NAME.get(b.name).order);
    const scanner = modules.filter((m) => !PLATFORM_BY_NAME.has(m.name));

    return (
      <div>
        {scanner.length > 0 && (
          <section className="mb-4">
            <GroupHeading
              title="Unified Scanner"
              note="Modules the Pipsqueak desktop scanner runs. The switches under each one are its individual checks."
            />
            {scanner.map((module) => (
              <ModuleRow key={`${licenseName}-${module.name}`} licenseName={licenseName} module={module} />
            ))}
          </section>
        )}
        {platform.length > 0 && (
          <section className="mb-2">
            <GroupHeading
              title="Platform Features"
              note="Parts of the Pipsqueak web app this plan includes. Switched off, a feature stays visible to the tenant with a lock and an upgrade prompt, and its server routes refuse requests."
            />
            {platform.map((module) => {
              const meta = PLATFORM_BY_NAME.get(module.name);
              return (
                <ModuleRow key={`${licenseName}-${module.name}`} licenseName={licenseName}
                           module={module} label={meta.label} desc={meta.desc} />
              );
            })}
          </section>
        )}
      </div>
    );
  };

  return (
    <div className="container py-4" style={themeStyles}>
      <Row className="justify-content-center">
        <Col md={10}>
          <Card className="shadow-sm" style={cardThemeStyles}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className={isDarkMode ? "text-light" : "text-muted"}>
                  Choose what each plan includes: the desktop scanner's modules and the web app's features.
                </span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={fetchModules}
                  disabled={loading}
                >
                  <TbRefresh size={16} className="me-1" />
                  Refresh
                </Button>
              </div>

              {loading && (
                <div className="text-center my-3">
                  <Spinner animation="border" />
                </div>
              )}

              {!loading && (
                <Tab.Container activeKey={activeLicense} onSelect={setActiveLicense}>
                  <Nav
                    variant="pills"
                    className={`mb-4 ${isDarkMode ? "nav-pills-dark" : ""}`}
                    style={{
                      borderBottom: isDarkMode
                        ? "1px solid #444"
                        : "1px solid #dee2e6",
                    }}
                  >
                    {licenseModules.map((license) => {
                      const isActive = activeLicense === license.name;
                      const Icon = isActive ? TbShieldCheck : TbShield;
                      return (
                        <Nav.Item key={license.name}>
                          <Nav.Link
                            eventKey={license.name}
                            className={`d-flex align-items-center ${
                              isDarkMode ? "text-light" : "text-dark"
                            }`}
                            style={{
                              backgroundColor: isActive
                                ? "var(--v2-accent)"
                                : "transparent",
                              color: isActive
                                ? isDarkMode
                                  ? "#000"
                                  : "#fff"
                                : "var(--v2-text)",
                              border: "none",
                              borderRadius: "0.375rem 0.375rem 0 0",
                            }}
                          >
                            <Icon size={18} className="me-2" />
                            {license.displayName || license.name}
                            <span className="badge bg-secondary ms-2">
                              {(license.modules || []).length}
                            </span>
                          </Nav.Link>
                        </Nav.Item>
                      );
                    })}
                  </Nav>

                  <Tab.Content>
                    {licenseModules.map((license) => (
                      <Tab.Pane key={license.name} eventKey={license.name}>
                        <div className="mb-3">
                          <h6
                            className={`${isDarkMode ? "text-light" : "text-muted"} mb-3`}
                          >
                            <TbShieldCheck size={20} className="me-2" />
                            {license.displayName || license.name} plan
                          </h6>
                          <p
                            className={`small ${
                              isDarkMode ? "text-light" : "text-muted"
                            }`}
                          >
                            Everything a {license.displayName || license.name} workspace can use.
                            Changes reach tenants within about 30 seconds.
                          </p>
                        </div>
                        <ModuleList
                          licenseName={license.name}
                          modules={license.modules || []}
                        />
                      </Tab.Pane>
                    ))}
                  </Tab.Content>
                </Tab.Container>
              )}

              {message && (
                <Alert variant="success" className="mt-3">
                  {message}
                </Alert>
              )}
              {error && (
                <Alert variant="danger" className="mt-3">
                  {error}
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ScanModules;
